import {Alert, FlexColumn, Heading, Separator, Table, IconTooltip} from '@forecast-it/design-system';
import React, {useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import styled from 'styled-components';
import {currencies} from '../../../constants';
import Util from '../../shared/util/util';

const AlertContainer = styled.div`
	margin: 16px 0px;
`;

const HeadingContainer = styled.div`
	margin: 12px 0px;
`;

interface Company {
	id: string;
	currency: string;
}

interface ExchangeRate {
	id: string;
	currency: string;
	rate: number | null;
	isUsed: boolean | null;
	createdAt: string | null;
}

interface ExchangeRateRow extends ExchangeRate {
	title: string;
	subtitle: string;
	ratePrimary: string;
	rateSecondary: string;
	createdAtFormatted: string;
}

export const Currencies = ({company, exchangeRates}: {company: Company; exchangeRates: ExchangeRate[]}) => {
	const {formatMessage} = useIntl();
	const [isDismissed, setIsDismissed] = useState(false);

	const data = useMemo(
		() =>
			exchangeRates.map(exchangeRate => {
				const currency = currencies.find(c => c.code === exchangeRate.currency);

				const rate = exchangeRate.rate ?? 0;

				return {
					title: currency ? `${currency.code} (${currency.symbol})` : exchangeRate.currency,
					subtitle: currency ? currency.name : '',
					ratePrimary: `${rate} ${exchangeRate.currency}`,
					rateSecondary: `1 ${company.currency} = ${rate} ${exchangeRate.currency}`,
					...exchangeRate,
					createdAtFormatted: Util.formatUtcDateTimeToLocalTimezone(exchangeRate.createdAt),
				};
			}),
		[exchangeRates, company]
	);

	return (
		<FlexColumn>
			{!isDismissed && (
				<AlertContainer>
					<Alert
						text={formatMessage({id: 'settings_finance.currencies.info_banner'})}
						type={Alert.TYPE.INFORMATION}
						onDismiss={() => setIsDismissed(true)}
					/>
				</AlertContainer>
			)}

			<HeadingContainer>
				<FlexColumn gap="l">
					<Heading size={4}>{formatMessage({id: 'settings_finance.currencies.page_title'})}</Heading>
					<Separator />
				</FlexColumn>
			</HeadingContainer>

			<Table width={'100%'} data={data}>
				<Table.TitleSubtitleColumn
					accessorKey="currencyCodeSymbol"
					header={formatMessage({id: 'common.currency'})}
					allowSorting
					renderTitle={(row: ExchangeRateRow) => row.title}
					renderSubtitle={(row: ExchangeRateRow) => row.subtitle}
					renderTitleAccessory={(row: ExchangeRateRow) =>
						row.isUsed ? (
							<IconTooltip icon="lockClosed" iconColor="onlight">
								<IconTooltip.Item
									details={formatMessage({id: 'settings_finance.currencies.locked_tooltip.description'})}
								/>
							</IconTooltip>
						) : null
					}
				/>
				<Table.TitleSubtitleColumn
					accessorKey="ratePrimary"
					header={formatMessage({id: 'settings_finance.currencies.table.currency_rate'})}
					allowSorting
					renderTitle={(row: ExchangeRateRow) => row.ratePrimary}
					renderSubtitle={(row: ExchangeRateRow) => row.rateSecondary}
				/>
				<Table.TextColumn
					accessorKey="createdAtFormatted"
					header={formatMessage({id: 'global_filter_field.created_at'})}
					allowSorting
					sortingFn="text"
				/>
				<Table.ActionColumn header={formatMessage({id: 'common.actions'})} hideHeader>
					<Table.ActionColumn.Option onClick={() => undefined}>
						<FormattedMessage id="settings_finance.currencies.table.view_add_rate" />
					</Table.ActionColumn.Option>
					<Table.ActionColumn.Option onClick={() => undefined}>
						<FormattedMessage id="common.delete" />
					</Table.ActionColumn.Option>
				</Table.ActionColumn>
			</Table>
		</FlexColumn>
	);
};
